// Gabon Timezone Utilities
// Gabon uses WAT (West Africa Time) = UTC+1

export const GABON_TIMEZONE = 'Africa/Libreville';

// Get current time in Gabon
export const getGabonTime = () => {
    return new Date().toLocaleString('fr-FR', { timeZone: GABON_TIMEZONE });
};

// Get current date in Gabon
export const getGabonDate = () => {
    return new Date().toLocaleDateString('fr-FR', { timeZone: GABON_TIMEZONE });
};

// Get current time string (HH:MM) in Gabon
export const getGabonTimeString = () => {
    return new Date().toLocaleTimeString('fr-FR', { 
        timeZone: GABON_TIMEZONE,
        hour: '2-digit', 
        minute: '2-digit' 
    });
};

// Get current time with seconds (HH:MM:SS) in Gabon
export const getGabonTimeWithSeconds = () => {
    return new Date().toLocaleTimeString('fr-FR', { 
        timeZone: GABON_TIMEZONE,
        hour: '2-digit', 
        minute: '2-digit',
        second: '2-digit'
    });
};

// Get full date string in Gabon (e.g., "jeudi 5 mars")
export const getGabonFullDate = () => {
    return new Date().toLocaleDateString('fr-FR', { 
        timeZone: GABON_TIMEZONE,
        weekday: 'long', 
        day: 'numeric', 
        month: 'long' 
    });
};

// Get short date string in Gabon (e.g., "Jeu 5 Mars")
export const getGabonShortDate = () => {
    return new Date().toLocaleDateString('fr-FR', { 
        timeZone: GABON_TIMEZONE,
        weekday: 'short', 
        day: 'numeric', 
        month: 'short' 
    });
};

// Format a date to Gabon timezone
export const formatToGabon = (date, options = {}) => {
    const defaultOptions = {
        timeZone: GABON_TIMEZONE,
        ...options
    };
    return new Date(date).toLocaleString('fr-FR', defaultOptions);
};

// Format date for display (e.g., "05/03/2026 14:30")
export const formatGabonDateTime = (date) => {
    return new Date(date).toLocaleString('fr-FR', {
        timeZone: GABON_TIMEZONE,
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    });
};

// Get ISO date string for Gabon (YYYY-MM-DD)
export const getGabonISODate = () => {
    const now = new Date();
    const gabonDate = new Date(now.toLocaleString('en-US', { timeZone: GABON_TIMEZONE }));
    return gabonDate.toISOString().split('T')[0];
};

// Check if it's morning/afternoon/evening in Gabon
export const getGabonGreeting = () => {
    const hour = parseInt(new Date().toLocaleTimeString('fr-FR', { 
        timeZone: GABON_TIMEZONE, 
        hour: '2-digit',
        hour12: false 
    }));
    
    if (hour < 12) return 'Bonjour';
    if (hour < 18) return 'Bon après-midi';
    return 'Bonsoir';
};

// Get a Date object adjusted to Gabon timezone
// This creates a Date that represents the current moment in Gabon
export const getGabonNow = () => {
    // Get current time in Gabon timezone as a string
    const gabonTimeStr = new Date().toLocaleString('en-US', { timeZone: GABON_TIMEZONE });
    // Parse it back to a Date object
    return new Date(gabonTimeStr);
};

// Get the day of week in Gabon (0=Sunday, 1=Monday, etc.)
export const getGabonDayOfWeek = () => {
    return getGabonNow().getDay();
};

// Get today's date as ISO string (YYYY-MM-DD) in Gabon timezone
export const getGabonTodayISO = () => {
    const now = getGabonNow();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

// Convert any date to Gabon timezone Date object
export const toGabonDate = (date) => {
    const d = new Date(date);
    const gabonTimeStr = d.toLocaleString('en-US', { timeZone: GABON_TIMEZONE });
    return new Date(gabonTimeStr);
};

// Get weekday name in French for a date in Gabon timezone
export const getGabonWeekdayName = (date, format = 'long') => {
    return new Date(date).toLocaleDateString('fr-FR', {
        timeZone: GABON_TIMEZONE,
        weekday: format
    });
};

// Get day of week (0-6) for a date string (YYYY-MM-DD) without timezone conversion issues
// This parses the date as local date components, not as UTC
export const getDayOfWeekFromDateString = (dateStr) => {
    // Parse YYYY-MM-DD directly to avoid timezone issues
    const [year, month, day] = dateStr.split('-').map(Number);
    // Note: month is 0-indexed in JavaScript Date
    const d = new Date(year, month - 1, day);
    return d.getDay();
};

// Get day number from date string (YYYY-MM-DD) without timezone issues
export const getDayFromDateString = (dateStr) => {
    const [, , day] = dateStr.split('-').map(Number);
    return day;
};

